import { Box, Typography } from "@mui/material";
import RecentlyFoundLabel, {
  isRecentlyFound,
} from "../../../posture/controls/components/RecentlyFoundLabel";
import AffectedAssetsCell from "../../../posture/vulnerabilities/components/AffectedAssetsCell";
import ChipColor from "../../../remediation/plan/components/ChipColor";
import IntegrationImage from "../../integrations/components/IntegrationImage";
import { IntegrationsMapping } from "../../integrations/IntegrationsMapping";
import EntityFavoritesButton from "../components/EntityDrawer/EntityFavoritesButton";
import { getRiskNameFromValue } from "../utils";
import LabelsCell from "./LabelsCell/LabelsCell";
import TagCell from "./TagCell/TagCell";

export const getSharedColumns = (assets) => {
  return [
    {
      field: "display_name",
      headerName: "Name",
      flex: 2,
      minWidth: 180,
      align: "left",
      headerAlign: "left",
      disableReorder: true,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <EntityFavoritesButton entityId={params.row?.entity_id} icon={true} />

          <Typography variant="caption" sx={{ flexGrow: 1 }}>
            {params.value}
          </Typography>
          {isRecentlyFound(
            params.row?.first_collected_timestamp?.timestamp_utc
          ) && (
            <RecentlyFoundLabel
              foundTimestamp={
                params.row?.first_collected_timestamp?.timestamp_utc || ""
              }
            />
          )}
        </Box>
      ),
    },
    {
      field: "risk",
      headerName: "Risk",
      minWidth: 90,
      flex: 0.5,
      align: "left",
      headerAlign: "left",
      disableReorder: true,
      resizable: false,
      valueFormatter: (params) => getRiskNameFromValue(params.value),
      renderCell: (params) => (
        <ChipColor
          value={params.value}
          type={"entity_risk"}
          width={62}
          style={{ padding: "6px", borderRadius: "8px" }}
          useShortLabel
        />
      ),
    },
    {
      field: "sources",
      headerName: "Source",
      minWidth: 120,
      flex: 1,
      align: "left",
      headerAlign: "left",
      disableReorder: true,
      sortComparator: (v1, v2) => {
        if (v1.length !== v2.length) {
          return v1.length - v2.length;
        }
        const v1Sorted = [...v1].sort();
        const v2Sorted = [...v2].sort();
        return v1Sorted.join(",").localeCompare(v2Sorted.join(","));
      },
      valueFormatter: (params) =>
        params.value
          .map((source) => IntegrationsMapping[source]?.displayName)
          .join(", "),
      renderCell: (params) => {
        // TODO - make this better! reactive instead of const max tool (like the related assets cell), and tooltip on the number
        const maxToolsDisplay = 4;
        return (
          <>
            {params.value
              .map((integration) => (
                <IntegrationImage
                  key={integration}
                  name={integration}
                  width={"40px"}
                />
              ))
              .slice(0, maxToolsDisplay)}
            {params.value.length > maxToolsDisplay
              ? `+${params.value.length - maxToolsDisplay}`
              : null}
          </>
        );
      },
    },
    {
      field: "related_asset_ids",
      headerName: "Part of Critical Assets",
      minWidth: 230,
      flex: 2,
      align: "left",
      headerAlign: "left",
      disableReorder: true,
      valueGetter: (params) =>
        assets?.filter((asset) =>
          asset.entity_ids.includes(params.row.entity_id)
        ),
      valueFormatter: (params) =>
        params.value?.map((asset) => asset.name).join(", ") || "-",
      sortComparator: (v1, v2) => {
        if (v1.length !== v2.length) {
          return v2.length - v1.length;
        }
        const v1Sorted = [...v1.map((asset) => asset.name)].sort();
        const v2Sorted = [...v2.map((asset) => asset.name)].sort();
        return v1Sorted.join(",").localeCompare(v2Sorted.join(","));
      },
      renderCell: (params) => (
        <>
          {params.value?.length > 0 ? (
            <Box
              onClick={(event) => {
                event.stopPropagation();
              }}
              width="100%"
            >
              <AffectedAssetsCell assets={params.value} />
            </Box>
          ) : (
            <Typography variant="caption" sx={{ paddingLeft: 1 }}>
              -
            </Typography>
          )}
        </>
      ),
    },
    {
      field: "labels",
      headerName: "Label",
      minWidth: 160,
      flex: 1.5,
      align: "left",
      headerAlign: "left",
      disableReorder: true,
      valueGetter: (params) => params.row?.labels || [],
      valueFormatter: (params) =>
        params.value.length > 0 ? params.value.join(", ") : "-",
      sortComparator: (v1, v2) => {
        if (v1.length !== v2.length) {
          return v2.length - v1.length;
        }
        const v1Sorted = [...v1].sort();
        const v2Sorted = [...v2].sort();
        return v1Sorted.join(",").localeCompare(v2Sorted.join(","));
      },
      renderCell: (params) => {
        if (params.value.length === 0) {
          return (
            <Typography variant="caption" sx={{ paddingLeft: 1 }}>
              -
            </Typography>
          );
        }
        return (
          <Box
            onClick={(event) => {
              event.stopPropagation();
            }}
            width="100%"
          >
            <LabelsCell entity={params.row} />
          </Box>
        );
      },
    },
    {
      field: "custom_tags",
      headerName: "Tag",
      minWidth: 160,
      flex: 1.5,
      align: "left",
      headerAlign: "left",
      disableReorder: true,
      valueGetter: (params) => params.row?.custom_tags || [],
      valueFormatter: (params) =>
        params.value.length > 0 ? params.value.join(", ") : "-",
      sortComparator: (v1, v2) => {
        if (v1.length !== v2.length) {
          return v2.length - v1.length;
        }
        const v1Sorted = [...v1].sort();
        const v2Sorted = [...v2].sort();
        return v1Sorted.join(",").localeCompare(v2Sorted.join(","));
      },
      renderCell: (params) => {
        if (params.value.length === 0) {
          return (
            <Typography variant="caption" sx={{ paddingLeft: 1 }}>
              -
            </Typography>
          );
        }
        return (
          <Box
            onClick={(event) => {
              event.stopPropagation();
            }}
            width="100%"
          >
            <TagCell entity={params.row} />
          </Box>
        );
      },
    },
  ];
};
